/**
 * apps/admin/src/main.tsx · Admin Console 入口
 *
 * 承接：
 * - T11 V0.3.2 §10 前端栈（React 18 + Vite + Tailwind v3 + Shadcn/ui + TanStack Router/Query）
 * - T11 §11.1 Error Boundary 层级（Sentry.ErrorBoundary 顶层）
 * - T11 §11.3 Sentry 接入 + before_send PII（client side 复用 packages/shared 思路 · S2 落 ts redactor）
 * - T11 §12 PostHog 埋点骨架（PR-C 实施 9 admin events 集中 schema）
 * - DAC-01 §4.D Biome 锁定 + ADR-505 单主路径
 *
 * PR-A 范围：基础栈跑通 + hello world 路由 + Sentry/PostHog DSN 空跳过
 * PR-B 加：Toaster + M01 业务页 + admin/v1/sessions 联调
 * PR-C 加：PostHog 9 events 集中 schema + capture wrapper
 */
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import ReactDOM from "react-dom/client";

import { registerExternalTestsFixtures } from "./lib/fixtures/external-tests";
import { registerLoopOverviewFixtures } from "./lib/fixtures/loop-overview";
import { registerM01Fixtures } from "./lib/fixtures/m01";
import { registerM02Fixtures } from "./lib/fixtures/m02";
import { registerM03Fixtures } from "./lib/fixtures/m03";
import { registerM04Fixtures } from "./lib/fixtures/m04";
import { registerM05Fixtures } from "./lib/fixtures/m05";
import { registerM07Fixtures } from "./lib/fixtures/m07";
import { registerM09Fixtures } from "./lib/fixtures/m09";
import { registerP2Fixtures } from "./lib/fixtures/p2";
import { registerSnapshotFixtures } from "./lib/fixtures/snapshot";
import { MOCK_MODE } from "./lib/mock-mode";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// Mock 模式启动时注册各模块 fixture（W2-9 数据工厂 · CLAUDE.md §11.5.1）
if (MOCK_MODE) {
	registerExternalTestsFixtures();
	registerP2Fixtures();
	registerLoopOverviewFixtures();
	registerM01Fixtures();
	registerM02Fixtures();
	registerM03Fixtures();
	registerM04Fixtures();
	registerM05Fixtures();
	registerM07Fixtures();
	registerM09Fixtures();
	registerSnapshotFixtures();
}

if (import.meta.env.VITE_SENTRY_DSN) {
	Sentry.init({
		dsn: import.meta.env.VITE_SENTRY_DSN,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "development",
		tracesSampleRate: 0.1,
		integrations: [Sentry.browserTracingIntegration()],
	});
}

if (import.meta.env.VITE_POSTHOG_KEY) {
	posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
		api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com",
		capture_pageview: true,
	});
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000,
			retry: 1,
		},
	},
});

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const rootElement = document.getElementById("root");
if (!rootElement) {
	throw new Error("Root element #root not found in index.html");
}

ReactDOM.createRoot(rootElement).render(
	<React.StrictMode>
		<Sentry.ErrorBoundary
			fallback={
				<div className="flex min-h-screen items-center justify-center bg-surface-background">
					<div className="text-text-secondary">应用启动失败 · 请刷新页面或联系支持</div>
				</div>
			}
		>
			<PostHogProvider client={posthog}>
				<QueryClientProvider client={queryClient}>
					<RouterProvider router={router} />
				</QueryClientProvider>
			</PostHogProvider>
		</Sentry.ErrorBoundary>
	</React.StrictMode>,
);
